import React, { useMemo } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import "./RichTextEditor.css";
import { RICH_TEXT_TOOLBAR } from "./editorConfig";

const getImageDimensions = (source) =>
  new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve({ width: image.width, height: image.height });
    image.onerror = () => resolve({ width: undefined, height: undefined });
    image.src = source;
  });

class LocalImageUploadAdapter {
  constructor(loader, onImageAdded) {
    this.loader = loader;
    this.onImageAdded = onImageAdded;
  }

  async upload() {
    const file = await this.loader.file;
    const source = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    const dimensions = await getImageDimensions(source);

    this.onImageAdded?.({ file, title: file.name, ...dimensions });
    return { default: source };
  }

  abort() {}
}

const RichTextEditor = ({ value = "", onChange, onImageAdded }) => {
  const config = useMemo(() => {
    class LocalImageUploadPlugin {
      constructor(editor) {
        this.editor = editor;
      }

      init() {
        this.editor.plugins.get("FileRepository").createUploadAdapter = (
          loader,
        ) => new LocalImageUploadAdapter(loader, onImageAdded);
      }
    }

    return {
      toolbar: RICH_TEXT_TOOLBAR,
      image: {
        toolbar: [
          "imageTextAlternative",
          "toggleImageCaption",
          "imageStyle:inline",
          "imageStyle:block",
          "imageStyle:side",
        ],
        resizeOptions: [
          { name: "resizeImage:original", value: null, label: "Original" },
          { name: "resizeImage:25", value: "25", label: "25%" },
          { name: "resizeImage:50", value: "50", label: "50%" },
          { name: "resizeImage:75", value: "75", label: "75%" },
        ],
        resizeUnit: "%",
      },
      table: {
        contentToolbar: ["tableColumn", "tableRow", "mergeTableCells"],
      },
      link: {
        addTargetToExternalLinks: true,
        defaultProtocol: "https://",
      },
      extraPlugins: [LocalImageUploadPlugin],
    };
  }, [onImageAdded]);

  return (
    <div className="rich-text-editor" data-testid="rich-text-editor">
      <CKEditor
        editor={ClassicEditor}
        data={value}
        config={config}
        onChange={(_, editor) => onChange(editor.getData())}
      />
    </div>
  );
};

export default RichTextEditor;
